// Top-level router. All routes except /login are protected.

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ProtectedRoute } from './components/ProtectedRoute';
import { AppShell } from './components/AppShell';
import { BrandSplash } from './components/BrandSplash';

// Route pages are code-split (IMP-046): each becomes its own chunk loaded on
// demand, so the initial bundle carries only the shell + the landing route
// instead of all 28 pages. The `.then(m => ({ default: m.X }))` shim adapts our
// named exports to the default-export shape React.lazy expects. ComingSoonPage
// and NotFoundPage stay statically imported — they're tiny and serve as
// synchronous fallbacks (FeatureGate / catch-all), so a lazy chunk + loading
// flash for them would be counterproductive.
const LoginPage = lazy(() => import('./routes/LoginPage').then((m) => ({ default: m.LoginPage })));
const HomePage = lazy(() => import('./routes/HomePage').then((m) => ({ default: m.HomePage })));
const DoorsPage = lazy(() => import('./routes/DoorsPage').then((m) => ({ default: m.DoorsPage })));
const DirectoryPage = lazy(() => import('./routes/DirectoryPage').then((m) => ({ default: m.DirectoryPage })));
const OrgChartPage = lazy(() => import('./routes/OrgChartPage').then((m) => ({ default: m.OrgChartPage })));
const TicketsPage = lazy(() => import('./routes/TicketsPage').then((m) => ({ default: m.TicketsPage })));
const NewTicketPage = lazy(() => import('./routes/NewTicketPage').then((m) => ({ default: m.NewTicketPage })));
const TicketDetailPage = lazy(() => import('./routes/TicketDetailPage').then((m) => ({ default: m.TicketDetailPage })));
const TicketMetricsPage = lazy(() => import('./routes/TicketMetricsPage').then((m) => ({ default: m.TicketMetricsPage })));
const FacilitiesSettingsPage = lazy(() => import('./routes/FacilitiesSettingsPage').then((m) => ({ default: m.FacilitiesSettingsPage })));
const EventsPage = lazy(() => import('./routes/EventsPage').then((m) => ({ default: m.EventsPage })));
const EventsSettingsPage = lazy(() => import('./routes/EventsSettingsPage').then((m) => ({ default: m.EventsSettingsPage })));
const AnnouncementsManagePage = lazy(() => import('./routes/AnnouncementsManagePage').then((m) => ({ default: m.AnnouncementsManagePage })));
const SurveysPage = lazy(() => import('./routes/SurveysPage').then((m) => ({ default: m.SurveysPage })));
const PurchaseRequestsPage = lazy(() => import('./routes/PurchaseRequestsPage').then((m) => ({ default: m.PurchaseRequestsPage })));
const PurchaseRequestSettingsPage = lazy(() => import('./routes/PurchaseRequestSettingsPage').then((m) => ({ default: m.PurchaseRequestSettingsPage })));
const NewPurchaseRequestPage = lazy(() => import('./routes/NewPurchaseRequestPage').then((m) => ({ default: m.NewPurchaseRequestPage })));
const PurchaseRequestDetailPage = lazy(() => import('./routes/PurchaseRequestDetailPage').then((m) => ({ default: m.PurchaseRequestDetailPage })));
const PurchaseOrderPage = lazy(() => import('./routes/PurchaseOrderPage').then((m) => ({ default: m.PurchaseOrderPage })));
const CarsPage = lazy(() => import('./routes/CarsPage').then((m) => ({ default: m.CarsPage })));
const OfficesPage = lazy(() => import('./routes/OfficesPage').then((m) => ({ default: m.OfficesPage })));
const OfficeDetailPage = lazy(() => import('./routes/OfficeDetailPage').then((m) => ({ default: m.OfficeDetailPage })));
const AircraftPage = lazy(() => import('./routes/AircraftPage').then((m) => ({ default: m.AircraftPage })));
const AircraftSettingsPage = lazy(() => import('./routes/AircraftSettingsPage').then((m) => ({ default: m.AircraftSettingsPage })));
const FlightRequestSeatPage = lazy(() => import('./routes/FlightRequestSeatPage').then((m) => ({ default: m.FlightRequestSeatPage })));
const FlightRequestNewPage = lazy(() => import('./routes/FlightRequestNewPage').then((m) => ({ default: m.FlightRequestNewPage })));
const ResourcesPage = lazy(() => import('./routes/ResourcesPage').then((m) => ({ default: m.ResourcesPage })));
const AdminLayout = lazy(() => import('./routes/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminUsersPage = lazy(() => import('./routes/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const AdminSystemPage = lazy(() => import('./routes/admin/AdminSystemPage').then((m) => ({ default: m.AdminSystemPage })));
const AdminFeedbackPage = lazy(() => import('./routes/admin/AdminFeedbackPage').then((m) => ({ default: m.AdminFeedbackPage })));
const LogsView = lazy(() => import('./routes/LogsPage').then((m) => ({ default: m.LogsView })));
const SettingsPage = lazy(() => import('./routes/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const NotificationsPage = lazy(() => import('./routes/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const FeedbackRoute = lazy(() => import('./routes/FeedbackRoute').then((m) => ({ default: m.FeedbackRoute })));

import { ComingSoonPage } from './routes/ComingSoonPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { useFeatureFlags, isFeatureEnabled, type FeatureFlags } from './lib/features';
import { useAuth } from './lib/auth';

function FeatureGate({
  feature,
  children,
}: {
  // Any flag in FeatureFlags is gateable — typing against keyof keeps this in
  // step with the flag set instead of drifting as a hand-maintained union.
  feature: keyof FeatureFlags;
  children: React.ReactNode;
}) {
  const { claims } = useAuth();
  const { flags, loading } = useFeatureFlags();

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-n-200 border-t-ol-orange" />
      </div>
    );
  }

  if (!isFeatureEnabled(feature, flags, claims)) {
    return <ComingSoonPage />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    // Suspense catches the first lazy-chunk load for routes rendered outside the
    // AppShell (login, the print-only PO sheet, 404) and the initial shell load.
    // Navigation *between* AppShell pages is covered by a second boundary around
    // the <Outlet/> in AppShell, so the chrome stays put and only the content
    // area shows a fallback.
    <Suspense fallback={<BrandSplash />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Purchase Order document — protected, but rendered WITHOUT the AppShell
          chrome so the browser print/Save-as-PDF output is a clean sheet. */}
      <Route
        path="/purchase-requests/:prId/po"
        element={
          <ProtectedRoute>
            <PurchaseOrderPage />
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="doors" element={<DoorsPage />} />
        <Route path="directory" element={<DirectoryPage />} />
        <Route path="directory/org-chart" element={<OrgChartPage />} />
        {/* Facilities is a live module; the `facilities` flag (default on)
            lets an admin take it down for maintenance. Each route is gated so
            a direct URL can't bypass the flag. */}
        <Route
          path="tickets"
          element={
            <FeatureGate feature="facilities">
              <TicketsPage />
            </FeatureGate>
          }
        />
        <Route
          path="tickets/new"
          element={
            <FeatureGate feature="facilities">
              <NewTicketPage />
            </FeatureGate>
          }
        />
        <Route
          path="tickets/metrics"
          element={
            <FeatureGate feature="facilities">
              <TicketMetricsPage />
            </FeatureGate>
          }
        />
        <Route
          path="tickets/settings"
          element={
            <FeatureGate feature="facilities">
              <FacilitiesSettingsPage />
            </FeatureGate>
          }
        />
        <Route
          path="tickets/:ticketId"
          element={
            <FeatureGate feature="facilities">
              <TicketDetailPage />
            </FeatureGate>
          }
        />
        <Route
          path="events/settings"
          element={
            <FeatureGate feature="events">
              <EventsSettingsPage />
            </FeatureGate>
          }
        />
        <Route
          path="events/*"
          element={
            <FeatureGate feature="events">
              <EventsPage />
            </FeatureGate>
          }
        />
        {/* Announcement management lives beside the content it controls (the
            Home block links here for contentAdmins); /announcements stays free
            for a future end-user archive. */}
        <Route path="announcements/manage" element={<AnnouncementsManagePage />} />
        <Route
          path="surveys/*"
          element={
            <FeatureGate feature="surveys">
              <SurveysPage />
            </FeatureGate>
          }
        />
        {/* Feedback form at a real URL so Announcements / any link can point
            at it (opens the shared modal; closing returns Home). */}
        <Route path="feedback" element={<FeedbackRoute />} />
        {/* Purchase Requests is a live module; the `purchaseRequests` flag
            (default on) lets an admin take it down for maintenance. Gated per
            route so a direct URL can't bypass the flag. (The print-only
            /purchase-requests/:prId/po sheet above stays ungated — it's a
            deep link reached only from the gated detail page.) */}
        <Route
          path="purchase-requests"
          element={
            <FeatureGate feature="purchaseRequests">
              <PurchaseRequestsPage />
            </FeatureGate>
          }
        />
        <Route
          path="purchase-requests/new"
          element={
            <FeatureGate feature="purchaseRequests">
              <NewPurchaseRequestPage />
            </FeatureGate>
          }
        />
        <Route
          path="purchase-requests/settings"
          element={
            <FeatureGate feature="purchaseRequests">
              <PurchaseRequestSettingsPage />
            </FeatureGate>
          }
        />
        <Route
          path="purchase-requests/:prId"
          element={
            <FeatureGate feature="purchaseRequests">
              <PurchaseRequestDetailPage />
            </FeatureGate>
          }
        />
        <Route
          path="cars/*"
          element={
            <FeatureGate feature="cars">
              <CarsPage />
            </FeatureGate>
          }
        />
        <Route path="offices" element={<OfficesPage />} />
        <Route path="offices/:siteCode" element={<OfficeDetailPage />} />
        <Route
          path="aircraft/request-seat/:flightId"
          element={
            <FeatureGate feature="aircraft">
              <FlightRequestSeatPage />
            </FeatureGate>
          }
        />
        <Route
          path="aircraft/request-new"
          element={
            <FeatureGate feature="aircraft">
              <FlightRequestNewPage />
            </FeatureGate>
          }
        />
        <Route
          path="aircraft/settings"
          element={
            <FeatureGate feature="aircraft">
              <AircraftSettingsPage />
            </FeatureGate>
          }
        />
        <Route
          path="aircraft/*"
          element={
            <FeatureGate feature="aircraft">
              <AircraftPage />
            </FeatureGate>
          }
        />
        <Route path="resources" element={<ResourcesPage />} />
        {/* Travel section — Cars and Aircraft keep their top-level paths
            for backward compat with existing deep links. Hotels is an
            outbound link (Engine) and has no in-app route. Car Rental
            Discounts were folded into the Book a Car page (IMP-024); the old
            /travel/car-rentals path redirects there to preserve bookmarks. */}
        <Route path="travel/car-rentals" element={<Navigate to="/cars" replace />} />
        {/* Admin Console — each former tab is a deep-linkable sub-route under
            one layout (gated systemAdmin, logs also securityAdmin). The old
            /admin/access URL redirects for bookmark back-compat, and so does
            /logs, where the access log lived before it moved in (issue 270). */}
        <Route path="logs" element={<Navigate to="/admin/logs" replace />} />
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="access" element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="system" element={<AdminSystemPage />} />
          <Route path="feedback" element={<AdminFeedbackPage />} />
          <Route path="logs" element={<LogsView />} />
        </Route>
        <Route path="settings" element={<SettingsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
